/**
 * Sentry 初期化ヘルパー
 *
 * VITE_SENTRY_DSN が設定されている場合のみ @sentry/react を動的ロードして初期化する。
 * 未設定（開発環境など）の場合は何もしない。
 * @sentry/react が node_modules に存在しない場合も静かに無視する。
 */

// Sentry SDK の最小限の型定義（@sentry/react 未インストール時も型チェックが通るよう内部定義）
interface SentryModule {
  init: (options: {
    dsn: string
    environment: string
    tracesSampleRate: number
  }) => void
  captureException: (
    error: Error,
    context?: { extra?: Record<string, unknown> },
  ) => string
}

/** Sentry が初期化済みかどうか */
let _sentryInitialized = false

/** 動的ロード済みの Sentry モジュールキャッシュ */
let _sentryModule: SentryModule | null = null

/** @sentry/react を動的ロードする（失敗時は null を返す） */
async function loadSentry(): Promise<SentryModule | null> {
  if (_sentryModule) return _sentryModule
  try {
    // 変数経由にすることで Vite の静的解析（vite:import-analysis）を回避し、
    // @sentry/react 未インストール時も 500 エラーではなく try/catch で処理される
    const pkg = '@sentry/react'
    const mod = await import(/* @vite-ignore */ pkg)
    _sentryModule = mod as unknown as SentryModule
    return _sentryModule
  } catch {
    return null
  }
}

/**
 * Sentry を初期化する。
 * VITE_SENTRY_DSN 未設定時は何もしない（エラーにならない）。
 */
export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) {
    return
  }

  const Sentry = await loadSentry()
  if (!Sentry) {
    console.debug('Sentry initialization skipped: @sentry/react not available')
    return
  }

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? 'development',
    tracesSampleRate: 0.1,
  })
  _sentryInitialized = true
}

/**
 * Sentry にエラーを送信する。
 * Sentry が初期化されていない場合は何もしない。
 */
export async function captureException(
  error: Error,
  extras?: Record<string, unknown>,
): Promise<void> {
  if (!_sentryInitialized) {
    return
  }

  const Sentry = await loadSentry()
  if (!Sentry) return

  Sentry.captureException(error, extras ? { extra: extras } : undefined)
}

/** Sentry が初期化済みかどうかを返す */
export function isSentryInitialized(): boolean {
  return _sentryInitialized
}
